import { rest } from '@0xtorch/core'
import { z } from 'zod'
import { fetchDataWithAutoRetry } from '../../utils/fetchDataWithAutoRetry'
import { bybitApiEndpoint } from '../constants'
import { commonResponseSchema, successReponseSchema } from '../schemas'
import { createPrivateGetHeaders } from '../utils'
import { type Withdrawal, withdrawalResultSchema } from './schema'

const responseSchema = successReponseSchema.extend({
  result: withdrawalResultSchema,
})

const urlPath = '/v5/asset/withdraw/query-record'

type FetchWithdrawalHistoriesParameters = {
  apiKey: string
  apiSecret: string
  from: number
  to: number
  limit: number
  proxy?: string
  headers?: Record<string, string>
}

/** endTime - startTime should be less than 30 days. Query last 30 days records by default. */
export const fetchWithdrawalHistories = ({
  apiKey,
  apiSecret,
  from,
  to,
  limit,
  proxy,
  headers,
}: FetchWithdrawalHistoriesParameters): Promise<readonly Withdrawal[]> =>
  fetchDataWithAutoRetry({
    from,
    to,
    limit,
    desc: true,
    fetchData: async ({ from, to, limit, desc }) => {
      if (!desc) {
        throw new Error('desc must be true')
      }
      if (limit > 50) {
        throw new Error('limit must be less than or equal to 50')
      }

      const url = new URL(urlPath, bybitApiEndpoint)
      url.searchParams.append('limit', limit.toString())
      url.searchParams.append('startTime', from.toString())
      url.searchParams.append('endTime', to.toString())
      const query = url.searchParams.toString()

      const response = await rest(
        proxy === undefined
          ? url.toString()
          : `${proxy}${encodeURIComponent(url.toString())}`,
        {
          fetchOptions: {
            method: 'GET',
            headers: {
              ...headers,
              ...(await createPrivateGetHeaders({
                apiKey,
                apiSecret,
                query: query.length > 0 ? query : '',
              })),
            },
          },
          schema: z.union([responseSchema, commonResponseSchema]),
        },
      )

      if (!('result' in response)) {
        throw new Error(response.retMsg)
      }
      return response.result.rows
    },
  })
